/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import {
  ListAdminOauthClientsData,
  ListAdminOauthClientsResponse,
  M2MClientSortableFields,
  PaginatedM2MClientsResponseSchema,
} from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { usePaginatedQuery } from '@/hooks/query/usePaginatedQuery';
import { jsonSchemaToTypeMap } from '@/utils/filters';

// M2M client filters schema - embedded in response meta, defined inline
const $M2MClientFilters = {
  properties: {
    name: { type: 'string' },
  },
} as const;

// Define the filters type based on the schema
export type M2MClientFilters = {
  name?: string;
};

// Type for individual M2M client from paginated response
export type M2MClient = PaginatedM2MClientsResponseSchema['data'][number];

export type UsePaginatedM2MClientsQueryParams = {
  params?: ListAdminOauthClientsData;
  type?: 'inMemory' | 'queryParams';
};

/**
 * Custom hook that fetches paginated M2M clients with filtering, sorting and real-time updates.
 * Combines React Query for data fetching with WebSocket subscriptions for real-time updates.
 *
 * @param options Configuration object:
 *   - params: Optional query parameters (filters, pagination, sort)
 *   - type: Storage type - 'inMemory' or 'queryParams' for URL persistence
 *
 * @returns Object containing:
 *   - data: Paginated M2M clients response
 *   - isLoading: Boolean for initial load state
 *   - isFetching: Boolean for any loading state
 *   - isError: Boolean for error state
 *   - error: Error object if request failed
 *   - updateQueryParams: Function to update filters, sort, and pagination
 */
export function usePaginatedM2MClientsQuery({ params, type }: UsePaginatedM2MClientsQueryParams = {}) {
  return usePaginatedQuery<
    ListAdminOauthClientsResponse,
    M2MClient,
    M2MClientFilters,
    M2MClientSortableFields,
    ListAdminOauthClientsData
  >({
    apiCall: mgmtPlaneAPIClient.adminOauthClients.listAdminOauthClients.bind(mgmtPlaneAPIClient.adminOauthClients),
    filterTypes: jsonSchemaToTypeMap($M2MClientFilters),
    queryKey: 'm2mClients',
    realtimeEvents: ['m2m-client:created', 'm2m-client:updated', 'm2m-client:deleted'],
    params: {
      ...params,
    },
    type,
  });
}
